import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatPercent, formatDecimal } from "@/lib/formatters";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import type { FatigueAdRow } from "./types";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

function getFrequencyColor(freq: number): string {
  if (freq >= 4) return "bg-red-500";
  if (freq >= 2.5) return "bg-amber-500";
  return "bg-emerald-500";
}

function getFrequencyBarWidth(freq: number): number {
  return Math.min(100, (freq / 6) * 100);
}

export function CreativeFatigueTable({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery<FatigueAdRow[]>({
    queryKey: ["dashboard-fatiguing-ads", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("fatiguing-ads", params));
      if (!res.ok) throw new Error("Failed to fetch fatigue data");
      return res.json();
    },
  });

  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Creative Fatigue" widgetType="table" isEditMode={isEditMode} isLoading={isLoading} error={error instanceof Error ? error.message : null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex flex-col items-center justify-center h-full text-center p-4" data-testid="empty-fatigue">
          <AlertTriangle className="h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm font-medium text-gray-700">No verified fatigue assessment</p>
          <p className="text-xs text-gray-400 mt-1">A period-level frequency report is needed to assess creative fatigue.</p>
        </div>
      ) : (
        <div className="overflow-auto h-full p-3 space-y-2" data-testid="table-fatigue">
          {(data || []).map((ad) => {
            const freq = ad.fatigueSignal.frequency;
            return (
              <div key={ad.id} className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50/50">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    <span className="text-xs font-medium text-gray-900 truncate">{ad.name}</span>
                  </div>
                  <span className="text-[10px] font-medium text-amber-700 whitespace-nowrap">Consider refreshing creative</span>
                </div>
                <div className="flex items-center gap-4 text-[11px] mb-2">
                  <div>
                    <span className="text-gray-400">Frequency: </span>
                    <span className="font-semibold text-gray-900">{formatDecimal(freq)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">CTR decline: </span>
                    <span className="font-semibold text-red-600">{formatPercent(ad.fatigueSignal.ctrDecline)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Recent CTR: </span>
                    <span className="text-gray-700">{formatPercent(ad.fatigueSignal.recentCTR)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Prior CTR: </span>
                    <span className="text-gray-700">{formatPercent(ad.fatigueSignal.priorCTR)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Spend: </span>
                    <span className="text-gray-700">{formatCurrency(ad.fatigueSignal.recentSpend)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-400 w-12">Freq.</span>
                  <div className="h-1.5 flex-1 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${getFrequencyColor(freq)}`}
                      style={{ width: `${getFrequencyBarWidth(freq)}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-500 w-8 text-right">{formatDecimal(freq)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardWidget>
  );
}
